<template>
  <div class="grid grid-cols-1 gap-y-3 justify-space-between align-items-start border border-solid border-gray-200 rounded-sm p-b-m relative bg-gray-50">
    <div class="grid grid-cols-1 text-center gap-x-sm align-items-start">
      <div class="flex p-sm flex-col align-items-center justify-center bg-gray-50 text-gray-700 font-heavy tracking-tight rounded-t-sm" :class="{'bg-yellow-100': isUncheckedOutPastEvent && !isCancelledLesson, 'text-yellow-800': isUncheckedOutPastEvent && !isCancelledLesson }">
        <div v-if="isCancelledLesson" class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-red-200 rounded-t-sm">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-red-900">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
          </svg>
          <div class="text-12 text-red-900 upper-label">Cancelled</div>
        </div>
        <div v-else-if="isUncheckedOutPastEvent" class="">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 text-yellow-800">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
        </div>
        <div v-else-if="isFullyCompletedLesson" class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-green-300 rounded-t-sm">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-green-900">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0" />
          </svg>
          <div class="text-12 text-green-900 upper-label">Complete</div>
        </div>
        <div v-else-if="isPendingCoachConfirmation" class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-yellow-300 rounded-t-sm">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" stroke="currentColor" class="w-6 h-6 text-yellow-600">
            <path d="M368 48h4c6.627 0 12-5.373 12-12V12c0-6.627-5.373-12-12-12H12C5.373 0 0 5.373 0 12v24c0 6.627 5.373 12 12 12h4c0 80.564 32.188 165.807 97.18 208C47.898 298.381 16 383.9 16 464h-4c-6.627 0-12 5.373-12 12v24c0 6.627 5.373 12 12 12h360c6.627 0 12-5.373 12-12v-24c0-6.627-5.373-12-12-12h-4c0-80.564-32.188-165.807-97.18-208C336.102 213.619 368 128.1 368 48zm-48 0c0 28.672-4.564 55.81-12.701 80H76.701C68.564 103.81 64 76.672 64 48h256zm-12.701 336H76.701C97.405 322.453 141.253 280 192 280s94.595 42.453 115.299 104z"/>
          </svg>
          <div class="text-12 text-yellow-900 upper-label">Coach Needs To Confirm</div>
        </div>
        <div v-else-if="isUpcomingLesson" class="absolute top-0 right-0 left-0 flex gap-x-sm justify-center items-center p-xs bg-blue-300 rounded-t-sm">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 text-blue-900">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
          <div class="text-12 text-blue-900 upper-label">Upcoming</div>
        </div>
        <div class="upper-label" :class="{'m-t-lg': isCancelledLesson || ((isFullyCompletedLesson || isUpcomingLesson) && !isUncheckedOutPastEvent) }">{{ eventDayOfWeek }}</div>
        <div class="text-18">{{ eventMonthDay }}</div>
        <div class="text-14 m-t-xxs">{{ eventTime }}</div>
        <div v-if="isUncheckedOutPastEvent && !isCancelledLesson" class="text-12 text-yellow-800 m-t-xs font-heavy flex flex-col gap-y-sm">
          <div v-if="isAdmin">
            You have not completed this lesson. Checkout now to send notes, mark the lesson as completed, and bill the participants.
          </div>
          <div v-else>
            This lesson is pending checkout. Please contact the coach to complete the checkout or cancel the lesson.
          </div>
          <div v-if="isAdmin">
            <component :is="forAppEmbed ? 'div' : 'a'"
                    :href="!forAppEmbed ? `/private_lessons/${event.private_lessons[0].id}/checkout` : undefined"
                    @click="handleCheckoutClick"
                    target="_blank">
              <button class="btn btn-warning btn-compact m-t-xxs"><i class="fa fa-info-circle"></i> Checkout Now</button>
            </component>
          </div>
        </div>
      </div>
      <div class="p-x-m bg-gray-50">
        <h2 class="m-t-sm m-b-none">
          <component :is="forAppEmbed ? 'div' : 'a'"
                    :href="!forAppEmbed ? `/events/${event.id}` : undefined"
                    @click="handleEventClick"
                    target="_blank">
            <div class="flex justify-space-between w-full text-left text-gray-600">
              <div>
                <div>Private Lesson</div>
                <h4 v-if="getBillingPlan(event)" class="font-semi m-y-none text-gray-600">{{ getBillingPlan(event).name }}</h4>
                <div v-if="getBillingPlan(event)" class="text-12 text-gray-600 m-t-xs">
                  <div>
                    <span>{{ formatAsCurrency(getBillingPlan(event).amount) }} per wrestler</span>
                  </div>
                  <div>
                    <span>{{ hasSlotsAvailable(event) ? `${getBillingPlan(event).max_wrestler_count - getConfirmedWrestlerCount(event)} / ${getBillingPlan(event).max_wrestler_count} slots available` : 'No slots available' }}</span>
                  </div>
                </div>
              </div>
              <div>
                <button class="btn btn-default btn-compact m-t-xxs"><i class="fa fa-info-circle"></i> Details</button>
              </div>
            </div>
          </component>
        </h2>
      </div>
    </div>
    <div v-if="event.location" class="flex gap-x-xs align-items-baseline m-t-sm p-x-m">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="w-4 h-4">
        <path fill-rule="evenodd" d="m7.539 14.841.003.003.002.002a.755.755 0 0 0 .912 0l.002-.002.003-.003.012-.009a5.57 5.57 0 0 0 .19-.153 15.588 15.588 0 0 0 2.046-2.082c1.101-1.362 2.291-3.342 2.291-5.597A5 5 0 0 0 3 7c0 2.255 1.19 4.235 2.292 5.597a15.591 15.591 0 0 0 2.046 2.082 8.916 8.916 0 0 0 .189.153l.012.01ZM8 8.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" clip-rule="evenodd" />
      </svg>
      <div style="word-break: break-word;" v-html="urlify(event.location, true, null)"></div>
    </div>
    <div class="m-t-sm p-x-m bg-gray-50 text-gray-600">
      <h3 v-if="coachBookings.length > 0" class="font-semi text-gray-600">Coach</h3>
      <div v-for="coachBooking in coachBookings" class="flex justify-space-between flex-wrap align-items-center m-t-sm gap-y-xs">
        <div class="flex gap-x-sm">
          <img :src="coachBooking.profile.avatar" class="profile-pic w-10 h-10 rounded-circle" />
          <h3 class="m-t-none m-b-xxs">
            {{ coachBooking.profile.display_name }} <spinner :inline="true" v-if="loading"></spinner>
          </h3>
        </div>
        <div class="flex-shrink-0">
          <div v-if="!isEditing(coachBooking)" class="flex align-items-center gap-x-sm">
            <div v-if="coachBooking.status === 'invited'" class="text-blue-600">
              <i class="fa fa-envelope-o"></i> Invited
            </div>
            <div v-else-if="coachBooking.status === 'confirmed'" class="text-green-700">
              <i class="text-green-600 fa fa-check"></i> Confirmed
            </div>
            <div v-else-if="coachBooking.status === 'cancelled'" class="text-red-700">
              <i class="text-red-600 fa fa-times"></i> Cancelled
            </div>
            <div v-else-if="coachBooking.status === 'declined'" class="text-orange-600">
              <i class="text-orange-600 fa fa-ban"></i> Declined
            </div>
            <div v-else-if="coachBooking.status === 'completed'" class="text-green-800">
              <i class="text-green-800 fa fa-trophy"></i> Completed
            </div>
            <div v-else class="text-gray-600">
              <span>Pending</span>
            </div>
            <button v-if="canModifyBooking(coachBooking)" class="btn btn-sm btn-default" @click.prevent="startEditing(coachBooking)">
              <i class="fa fa-pencil"></i>
            </button>
          </div>
          <div v-else>
            <div v-if="coachBooking.status === 'invited'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(coachBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(coachBooking, 'declined')"><i class="fa fa-times"></i> Decline</button>
            </div>
            <div v-else-if="coachBooking.status === 'confirmed'" class="flex gap-x-xs">
              <button v-if="!coachBooking.showingCancellationInput" class="btn btn-danger btn-outline btn-sm" @click="showCancellationInput(coachBooking)"><i class="fa fa-times"></i> Cancel</button>
              <div v-if="coachBooking.showingCancellationInput" class="m-t-xs">
                <input
                  v-model="coachBooking.cancellationReason"
                  type="text"
                  class="form-control"
                  size="30"
                  placeholder="Enter cancellation reason"
                />
                <div v-if="coachBooking.cancellationError" class="text-danger text-xs m-t-xxs">
                  {{ coachBooking.cancellationError }}
                </div>
                <div class="flex flex-col-xs-only gap-sm">
                  <button class="btn btn-default m-t-xs" @click="hideCancellationInput(coachBooking)">
                    Back
                  </button>
                  <button class="btn btn-danger m-t-xs" @click="confirmCancellation(coachBooking)">
                    Confirm Cancel
                  </button>
                </div>
              </div>
              <button v-if="!coachBooking.showingCancellationInput" class="btn btn-default btn-sm" @click="stopEditing(coachBooking)"><i class="fa fa-times"></i></button>
            </div>
            <div v-else-if="coachBooking.status === 'cancelled' || coachBooking.status === 'declined'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(coachBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(coachBooking)"><i class="fa fa-times"></i></button>
            </div>
            <div v-else class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(coachBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(coachBooking, 'declined')"><i class="fa fa-times"></i> Decline</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(coachBooking)"><i class="fa fa-times"></i></button>
            </div>
          </div>
        </div>
      </div>
      <hr/>
      <h3 v-if="wrestlerBookings.length > 0" class="font-semi text-gray-600">Wrestlers</h3>
      <div v-for="wrestlerBooking in wrestlerBookings" class="flex justify-space-between flex-wrap align-items-center m-t-sm gap-y-xs">
        <div class="flex gap-x-sm">
          <img v-if="wrestlerBooking.profile.avatar" :src="wrestlerBooking.profile.avatar" class="profile-pic" />
          <div v-else class="profile-pic" style="background: #E5E7EB;" aria-label="Wrestler avatar placeholder"></div>
          <div>
            <p class="m-t-none m-b-xxs">
              {{ wrestlerBooking.profile.display_name }} <spinner :inline="true" v-if="loading"></spinner>
            </p>
            <div class="text-sm text-gray-600 flex flex-col">
              <span v-if="wrestlerBooking.profile.weight_class">{{ wrestlerBooking.profile.weight_class }}</span>
              <span v-if="wrestlerBooking.profile.dob" class="">{{ ageFor(wrestlerBooking.profile.dob) }} years old</span>
              <span v-if="wrestlerBooking.profile.experience" class="">{{ wrestlerBooking.profile.experience }}</span>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0">
          <div v-if="!isEditing(wrestlerBooking)" class="flex align-items-center gap-x-sm">
            <div v-if="wrestlerBooking.status === 'invited'" class="text-blue-600">
              <i class="fa fa-envelope-o"></i> Invited
            </div>
            <div v-else-if="wrestlerBooking.status === 'confirmed'" class="text-green-700">
              <i class="text-green-600 fa fa-check"></i> Confirmed
            </div>
            <div v-else-if="wrestlerBooking.status === 'cancelled'" class="text-red-700">
              <i class="text-red-600 fa fa-times"></i> Cancelled
            </div>
            <div v-else-if="wrestlerBooking.status === 'declined'" class="text-orange-600">
              <i class="text-orange-600 fa fa-ban"></i> Declined
            </div>
            <div v-else-if="wrestlerBooking.status === 'completed'" class="text-green-800">
              <i class="text-green-800 fa fa-trophy"></i> Completed
            </div>
            <div v-else class="text-gray-600">
              <span>Pending</span>
            </div>
            <button v-if="canModifyBooking(wrestlerBooking)" class="btn btn-sm btn-default" @click.prevent="startEditing(wrestlerBooking)">
              <i class="fa fa-pencil"></i>
            </button>
          </div>
          <div v-else>
            <div v-if="wrestlerBooking.status === 'invited'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(wrestlerBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(wrestlerBooking, 'declined')"><i class="fa fa-times"></i> Decline</button>
            </div>
            <div v-else-if="wrestlerBooking.status === 'confirmed'" class="flex gap-x-xs">
              <button v-if="!wrestlerBooking.showingCancellationInput" class="btn btn-danger btn-outline btn-sm" @click="showCancellationInput(wrestlerBooking)"><i class="fa fa-times"></i> Cancel</button>
              <div v-if="wrestlerBooking.showingCancellationInput" class="m-t-xs">
                <input
                  v-model="wrestlerBooking.cancellationReason"
                  type="text"
                  class="form-control"
                  size="30"
                  placeholder="Enter cancellation reason"
                />
                <div v-if="wrestlerBooking.cancellationError" class="text-danger text-xs m-t-xxs">
                  {{ wrestlerBooking.cancellationError }}
                </div>
                <div class="flex flex-col-xs-only gap-sm">
                  <button class="btn btn-default m-t-xs" @click="hideCancellationInput(wrestlerBooking)">
                    Back
                  </button>
                  <button class="btn btn-danger m-t-xs" @click="confirmCancellation(wrestlerBooking)">
                    Confirm Cancel
                  </button>
                </div>
              </div>
              <button v-if="!wrestlerBooking.showingCancellationInput" class="btn btn-default btn-sm" @click="stopEditing(wrestlerBooking)"><i class="fa fa-times"></i></button>
            </div>
            <div v-else-if="wrestlerBooking.status === 'cancelled' || wrestlerBooking.status === 'declined'" class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(wrestlerBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(wrestlerBooking)"><i class="fa fa-times"></i></button>
            </div>
            <div v-else class="flex gap-x-xs">
              <button class="btn btn-primary btn-outline btn-sm" @click="updateStatus(wrestlerBooking, 'confirmed')"><i class="fa fa-check"></i> Confirm</button>
              <button class="btn btn-danger btn-outline btn-sm" @click="updateStatus(wrestlerBooking, 'declined')"><i class="fa fa-times"></i> Decline</button>
              <button class="btn btn-default btn-sm" @click="stopEditing(wrestlerBooking)"><i class="fa fa-times"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Spinner from './spinner.vue';
import EventTime from '../../shared/event_time.vue';
import { errorableMix } from './mixins/errorable_mix';
import { urlHelperMixin } from './mixins/url_helper_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import { currencyMix } from '../../shared/currency_mix';
import { privateLessonMix } from './mixins/private_lesson_mix';
export default {
  components: { Spinner, EventTime },
  name: "EventWithEventBookings",
  mixins: [errorableMix, urlHelperMixin, appEmbedMix, currencyMix, privateLessonMix],
  props: {
    // This expects an event object with expanded event_booking json array
    initialEvent: {
      type: Object,
      required: true,
    },
    profilesEditable: {
      type: Array,
      default: () => [],
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
    currentProfileId: {
      type: Number,
      required: true,
    },
    currentProfileType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      event: null,
      bookings: [],
      editingBookingIds: {},
    }
  },
  created() {
    this.initEventData(this.initialEvent);
  },
  computed: {
    coachBookings() {
      return _.filter(this.bookings, (booking) => booking.profile_type === 'CoachProfile');
    },
    wrestlerBookings() {
      return _.filter(this.bookings, (booking) => booking.profile_type === 'WrestlerProfile');
    },
    isCancelledLesson() {
      return this.event.private_lessons[0].status === 'cancelled';
    },
    isPendingCoachConfirmation() {
      // Check if there are any wrestler bookings and at least one is in 'invited' status
      return this.coachBookings.length > 0 && this.coachBookings.some(booking => booking.status === 'invited');
    },
    isFullyCompletedLesson() {
      // Check if there are any wrestler bookings and all wrestler bookings are in 'completed' status
      return this.wrestlerBookings.length > 0 && this.wrestlerBookings.every(booking => booking.status === 'completed');
    },
    isUpcomingLesson() {
      return this.wrestlerBookings.length > 0 && this.wrestlerBookings.some(booking => booking.status === 'confirmed');
    },
    isUncheckedOutPastEvent() {
      // Check if the event is in the past
      const isPastEvent = moment().isAfter(this.eventStart);

      // Check if any of the bookings are not in 'completed' status
      const hasIncompleteBookings = this.bookings.some(booking => booking.status !== 'completed');

      return isPastEvent && hasIncompleteBookings;
    },
    eventStart() {
      if (this.event.all_day) {
        return moment(this.event.start_at.split('T')[0]);
      }

      return moment(this.event.start_at);
    },
    eventEnd() {
      if (this.event.all_day) {
        const ambiguousEnd = this.event.end_at.split('T')[0];
        // Subtract one day for 'human speak' UI display
        return moment(ambiguousEnd).subtract(1, 'day');
      }

      return moment(this.event.end_at);
    },
    multiDayEvent() {
      return this.eventEnd.diff(this.eventStart, 'days') >= 1;
    },
    eventMonthDay() {
      const eventMonthStart = this.eventStart.format('MMM');
      const eventMonthEnd = this.eventEnd.format('MMM');
      if (eventMonthEnd !== eventMonthStart) {
        if (this.eventStartYear !== this.eventEndYear) {
          return `${this.eventStart.format('MMM D')}, ${this.eventStartYear} - ${this.eventEnd.format('MMM D')}, ${this.eventEndYear}`
        }
        // If we are here the year is the same
        return `${this.eventStart.format('MMM D')} - ${this.eventEnd.format('MMM D')}, ${this.eventStartYear}`
      }

      if (this.multiDayEvent) {
        return `${this.eventStart.format('MMM D')} - ${this.eventEnd.format('D')}, ${this.eventStartYear}`;
      }

      return `${this.eventStart.format('MMM D')}, ${this.eventStartYear}`;
    },
    eventStartYear() {
      return this.eventStart.format('YYYY');
    },
    eventEndYear() {
      return this.eventEnd.format('YYYY');
    },
    eventDayOfWeek() {
      if (this.multiDayEvent) {
        return `${this.eventStart.format('ddd')}-${this.eventEnd.format('ddd')}`;
      }

      return this.eventStart.format('ddd');
    },
    eventTime() {
      if (this.event.all_day) {
        return "All day"
      }

      if (this.multiDayEvent) {
        return `${this.eventStart.format('M/D hh:mm a')} to ${this.eventEnd.format('M/D hh:mm a')}`;
      }

      return `${this.eventStart.format('hh:mm a')} to ${this.eventEnd.format('hh:mm a')}`;
    }
  },
  methods: {
    initEventData(event) {
      this.event = _.clone(event);
      this.bookings = _.clone(event.event_bookings);


      // Initialize editingBookingIds with all bookings set to false
      // For 'invited' status bookings, start in edit mode if they can be modified
      this.bookings.forEach(booking => {
        const startInEditMode = booking.status === 'invited' && this.canModifyBooking(booking);
        this.$set(this.editingBookingIds, booking.id, startInEditMode);
      });
    },
    ageFor(dob) {
      return moment().diff(moment(dob), 'years');
    },
    canModifyBooking(booking) {
      // Can't modify completed bookings
      if (booking.status === 'completed') {
        return false;
      }

      if (this.isAdmin) {
        return true;
      }

      // Check if user has edit rights for this booking
      return this.profilesEditable.some(profile =>
        profile.id === booking.profile_id && profile.profile_type === booking.profile_type
      );
    },
    isEditing(booking) {
      // Invited bookings are always in edit mode if they can be modified
      if (booking.status === 'invited' && this.canModifyBooking(booking)) {
        return true;
      }
      return this.editingBookingIds[booking.id] === true;
    },
    startEditing(booking) {
      this.$set(this.editingBookingIds, booking.id, true);
    },
    stopEditing(booking) {
      // For 'invited' status, don't allow canceling edit mode
      if (booking.status === 'invited') {
        return;
      }
      this.$set(this.editingBookingIds, booking.id, false);
    },
    updateStatus(booking, status, cancellationReason = null) {
      let vm = this;
      if (vm.loading) {
        return;
      }
      vm.loading = true;
      const url = vm.$apiService.eventBookingUrl(booking.id);
      const params = { event_booking: { status: status } };
      if (status === 'cancelled') {
        params.event_booking.cancelled_by_id = vm.currentProfileId;
        params.event_booking.cancelled_by_type = vm.currentProfileType;
        params.event_booking.cancellation_reason = cancellationReason;
      } else if (status === 'confirmed') {
        params.event_booking.booked_by_id = vm.currentProfileId;
        params.event_booking.booked_by_type = vm.currentProfileType;
        params.event_booking.cancellation_reason = "";
      }
      axios.put(url, params)
          .then((response) => {
            vm.$notificationManager.$emit('event-booking-updated');
            vm.$notificationManager.$emit('show-toast', 'Booking updated', true);

            // Reload event data to reflect any side effects
            vm.reloadEventData().then(() => {
              vm.loading = false;
            });
          })
          .catch((error) => {
            vm.loading = false;
            vm.$notificationManager.$emit('show-toast', 'Booking update failed', false);
          });
    },
    showCancellationInput(booking) {
      this.$set(booking, 'showingCancellationInput', true);
      this.$set(booking, 'cancellationReason', '');
      this.$set(booking, 'cancellationError', '');
    },
    hideCancellationInput(booking) {
      this.$set(booking, 'showingCancellationInput', false);
      this.$set(booking, 'cancellationError', '');
    },
    confirmCancellation(booking) {
      if (!booking.cancellationReason || booking.cancellationReason.trim() === '') {
        this.$set(booking, 'cancellationError', 'Cancellation reason is required.');
        return;
      }
      this.$set(booking, 'cancellationError', '');
      this.updateStatus(booking, 'cancelled', booking.cancellationReason);
      this.hideCancellationInput(booking);
    },
    reloadEventData() {
      const url = this.$apiService.eventUrlWithBookings(this.event.id);
      return axios.get(url)
          .then((response) => {
            this.initEventData(response.data);
          })
          .catch((error) => {
            console.error('Failed to reload event data:', error);
            this.$notificationManager.$emit('show-toast', 'Failed to reload event data', false);
          });
    },
    handleEventClick(e) {
      if (this.forAppEmbed) {
        e.preventDefault();
        this.askAppToPush(`${window.location.origin}/app_embeds/events/${this.event.id}?use_native=false`, 'View Event');
      }
    },
    handleCheckoutClick(e) {
      if (this.forAppEmbed) {
        e.preventDefault();
        this.askAppToPush(`${window.location.origin}/app_embeds/private_lessons/${this.event.private_lessons[0].id}/checkout`, 'Checkout');
      }
    }
  },
};
</script>
